<template>
  <div
    class="tw:flex tw:items-center tw:gap-8"
    :class="[
      labelFirst ? 'tw:flex-row-reverse tw:justify-between tw:w-full' :  'tw:w-fit',
      disabled ? 'tw:pointer-events-none' : '',
    ]"
  >
    <a-switch
      class="switch-base-antd tw:w-fit"
      :style="switchStyles"
      :disabled="disabled"
      v-model:checked="switchValue"
      @change="handleChange"
    />
    <div class="tw:flex tw:items-center tw:gap-6">
      <span class="tw:select-none tw:cursor-pointer" :class="labelClass" @click="handleLabelClick">
        {{ label }}
      </span>
      <div v-if="props.hint" class="tw:cursor-help" :title="props.hint">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8" r="7.333" stroke="#D9D9D9" stroke-width="1.333" />
          <path
            d="M8 11.333V8M8 4.667h.007"
            stroke="#D9D9D9"
            stroke-width="1.333"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watchEffect, computed } from 'vue'
// Props definition
interface Props {
  value?: boolean | string | number
  checked?: boolean | string | number
  width?: number | string
  height?: number | string
  handleSize?: number | string
  label?: string
  labelFirst?: boolean
  disabled?: boolean
  labelClass?: string
  hint?: string
}

const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  checked: undefined,
  width: 27,
  height: 16,
  handleSize: 12,
  label: '',
  labelFirst: false,
  disabled: false,
  labelClass: '',
  hint: '',
})

// Emits definition
const emit = defineEmits<{
  'update:value': [value: boolean]
  'update:checked': [value: boolean]
  change: [value: boolean]
}>()

// Internal state
const switchValue = ref<boolean>(false)

// Computed styles for switch
const switchStyles = computed(() => {
  const width = typeof props.width === 'number' ? `${props.width}px` : props.width
  const height = typeof props.height === 'number' ? `${props.height}px` : props.height
  const handleSize =
    typeof props.handleSize === 'number' ? `${props.handleSize}px` : props.handleSize

  return {
    '--switch-width': width,
    '--switch-height': height,
    '--switch-handle-size': handleSize,
  } as Record<string, string>
})

// Sync props to internal state
// Priority: checked > value > false
watchEffect(() => {
  if (props.checked !== undefined) {
    switchValue.value = Boolean(props.checked)
  } else if (props.value !== undefined) {
    switchValue.value = Boolean(props.value)
  } else {
    switchValue.value = false
  }
})

// Handle switch value change
const handleChange = (value: boolean) => {
  emit('update:value', value)
  emit('update:checked', value)
  emit('change', value)
}

// Handle label click (toggle switch when clicking on label)
const handleLabelClick = () => {
  if (!props.disabled) {
    // Emit like a switch click; flipping only the local state left v-model stale.
    switchValue.value = !switchValue.value
    handleChange(switchValue.value)
  }
}
</script>

<style lang="less" scoped>
.switch-base-antd {
  // Base dimensions - using CSS variables for dynamic sizing
  width: var(--switch-width, 27px);
  min-width: var(--switch-width, 27px);
  height: var(--switch-height, 16px);
  border-radius: 34px;
  background: var(--sds-color-background-surface-default);
  background-color: transparent;
  border: 1px solid var(--sds-color-border-default-secondary);

  // Checked state
  &.ant-switch-checked {
    background: var(--sds-color-background-surface-default) !important;
    background-color: transparent !important;
    border-color: var(--sds-color-border-default-primary);

    :deep(.ant-switch-handle) {
      inset-inline-start: calc(100% - var(--switch-handle-size, 12px) - 2px);
    }

    :deep(.ant-switch-handle::before) {
      background: var(--sds-color-border-brand-green) url('/assets/svg/v-switch.svg') no-repeat
        center;
      background-size: 50%;
    }
  }

  // Switch handle (thumb) - using CSS variables for dynamic sizing
  :deep(.ant-switch-handle) {
    width: var(--switch-handle-size, 12px);
    height: var(--switch-handle-size, 12px);
    top: 50%;
    transform: translateY(-50%);
    inset-inline-start: 2px;

    &::before {
      border-radius: 50%;
      background: var(--sds-color-icon-secondary-tertiary) url('/assets/svg/x-switch.svg') no-repeat
        center;
      background-size: 50%;
    }
  }

  // Hide inner text
  :deep(.ant-switch-inner) {
    display: none;
  }

  // Hover state
  &:hover:not(.ant-switch-disabled) {
    background-color: transparent;
  }

  // Disabled state
  &.ant-switch-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
